import {
  Annotation,
  AnnotationType,
  ArrowAnnotation,
  BoundingBox,
  CircleAnnotation,
  PenAnnotation,
  RectangleAnnotation,
  TextAnnotation,
} from '../types';
import { TEXT_MIN_WIDTH, TEXT_WIDTH_PER_CHAR_FACTOR } from '@v2/components/imageHandling/components/DrawingCanvas/constants';

export const getBBox = (a: Annotation): BoundingBox | null => {
  switch (a.type) {
    case AnnotationType.Pen:
    case AnnotationType.Highlighter: {
      const pen = a as PenAnnotation;
      if (pen.points.length < 2) return null;
      const xs = pen.points.map((p) => p.x);
      const ys = pen.points.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
    }
    case AnnotationType.Rectangle: {
      const r = a as RectangleAnnotation;
      return { x: r.origin.x, y: r.origin.y, width: r.width, height: r.height };
    }
    case AnnotationType.Circle: {
      const c = a as CircleAnnotation;
      return {
        x: c.center.x - c.radiusX,
        y: c.center.y - c.radiusY,
        width: c.radiusX * 2,
        height: c.radiusY * 2,
      };
    }
    case AnnotationType.Arrow: {
      const ar = a as ArrowAnnotation;
      return {
        x: Math.min(ar.start.x, ar.end.x),
        y: Math.min(ar.start.y, ar.end.y),
        width: Math.abs(ar.end.x - ar.start.x),
        height: Math.abs(ar.end.y - ar.start.y),
      };
    }
    case AnnotationType.Text: {
      const t = a as TextAnnotation;
      // SVG text positions `y` at the alphabetic baseline. Visible glyphs span from
      // `baseline - ASCENT_RATIO * fontSize` (top of caps) to `baseline + (1 - ASCENT_RATIO) * fontSize`
      // (descenders). Width approximation matches `AnnotationShape.renderText`.
      const ASCENT_RATIO = 0.8;
      const textWidth = Math.max(t.text.length * t.fontSize * TEXT_WIDTH_PER_CHAR_FACTOR, TEXT_MIN_WIDTH);
      return {
        x: t.position.x,
        y: t.position.y - t.fontSize * ASCENT_RATIO,
        width: textWidth,
        height: t.fontSize,
      };
    }
    default:
      return null;
  }
};
